import { useState, useCallback } from 'react';

export interface AppError {
  type: 'network' | 'api' | 'validation' | 'auth' | 'unknown';
  message: string;
  details?: string;
  code?: string | number;
  retryable: boolean;
}

interface UseErrorHandlerReturn {
  error: AppError | null;
  setError: (error: AppError | null) => void;
  handleError: (error: unknown, context?: string) => AppError;
  clearError: () => void;
  isRetryable: boolean;
}

/**
 * 統一エラーハンドリング用カスタムフック
 * - エラーの分類と正規化
 * - 再試行可能性の判定
 * - ユーザーフレンドリーなメッセージ変換
 * - エラー状態の管理
 */
export const useErrorHandler = (): UseErrorHandlerReturn => {
  const [error, setError] = useState<AppError | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const classifyError = useCallback((error: unknown, context?: string): AppError => {
    // ネットワークエラー
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        type: 'network',
        message: 'ネットワークエラーが発生しました',
        details: 'インターネット接続を確認してください',
        retryable: true
      };
    }

    // 認証エラー
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as any).status;
      if (status === 401 || status === 403) {
        return {
          type: 'auth',
          message: '認証エラーが発生しました',
          details: 'ログインし直してください',
          code: status,
          retryable: false
        };
      }

      // サーバーエラー
      if (status >= 500) {
        return {
          type: 'api',
          message: 'サーバーエラーが発生しました',
          details: 'しばらく時間をおいて再試行してください',
          code: status,
          retryable: true
        };
      }

      // クライアントエラー
      if (status >= 400) {
        return {
          type: 'api',
          message: 'リクエストエラーが発生しました',
          details: '入力内容を確認してください',
          code: status,
          retryable: false
        };
      }
    }

    // バリデーションエラー
    if (error && typeof error === 'object' && 'validationErrors' in error) {
      return {
        type: 'validation',
        message: '入力内容に問題があります',
        details: '必須項目が不足しているか、形式が正しくありません',
        retryable: false
      };
    }

    // Errorオブジェクト
    if (error instanceof Error) {
      return {
        type: 'unknown',
        message: error.message || '予期しないエラーが発生しました',
        details: context ? `コンテキスト: ${context}` : undefined,
        retryable: true
      };
    }

    // その他
    return {
      type: 'unknown',
      message: '予期しないエラーが発生しました',
      details: context ? `コンテキスト: ${context}` : undefined,
      retryable: true
    };
  }, []);

  const handleError = useCallback((error: unknown, context?: string): AppError => {
    const classifiedError = classifyError(error, context);
    setError(classifiedError);
    
    // エラーログ
    console.error(`[ErrorHandler] ${context || 'Unknown context'}:`, {
      originalError: error,
      classifiedError
    });

    return classifiedError;
  }, [classifyError]);

  return {
    error,
    setError,
    handleError,
    clearError,
    isRetryable: error?.retryable || false
  };
};

export default useErrorHandler;